import type { Paragraph } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  applyPerLineLeadingToFlow,
  buildCharRunMap,
  layoutShapeTextWithPerLineLeading,
  paragraphsToPlainText,
} from '@/lib/shapes/shape-text'
import { getShapeTextFlowBoundary } from '@/lib/boundaries/text-boundary'
import { layoutShapeTextWithDropCap } from '@/lib/shapes/shape-drop-cap'
import {
  buildShapeTextMeasurer,
  getSharedMeasureContext,
  gridRowPitchPt,
  rowGridBasisPt,
} from '@/lib/shapes/shape-text-measure'
import { splitShapeTextParagraphsAtOffset } from './helpers/split-shape-text-paragraphs'
import type { ParagraphBoxFit } from '@/lib/text/types/paragraph-box-fit'
import type { ParagraphBoxFitOptions } from '@/lib/text/types/paragraph-box-fit-options'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'
import { shapeTextFlowWithWrap } from '@/lib/shapes/shape-text-wrap-bands'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'

/** How much of `paragraphs` this shape lays out, and how much there was. `null` only when there is
 *  no text to lay out. A shape too small to hold a single row reports `consumedLength: 0` — it lays
 *  out nothing, which is not the same answer as "there was nothing to lay out" (WMPCM-9822). */
interface ShapeTextConsumption {
  consumedLength: number
  textLength: number
}

/**
 * One measurement pass, isolated so the verification pass below can repeat it against a DIFFERENT
 * paragraph list. That is the whole point: every input the scanline engine takes — the character
 * run map, the row pitch (`pointPt`/`leadingMult`, drawn from the largest run present) and hence the
 * flow itself — is derived from the paragraphs handed in, so measuring the full candidate and
 * measuring the kept half are not the same measurement.
 */
function measureShapeTextConsumption(
  paragraphs: Paragraph[],
  shape: ShapeData,
  fonts: ParagraphBlockFontLookup | undefined,
  wrap: ShapeTextWrapContext | undefined,
  fillToCharacter: boolean | undefined,
  /** Slice the shape at the grid THIS list implies instead of `paragraphs`'. Estimation only — see
   *  `growKeptOffset`; every other caller must leave it unset so basis and content agree. */
  gridBasis?: Paragraph[]
): ShapeTextConsumption | null {
  const text = paragraphsToPlainText(paragraphs)

  if (text.length === 0) return null

  const measurer = buildShapeTextMeasurer(paragraphs, fonts)
  const basis = gridBasis ? rowGridBasisPt(gridBasis, fonts) : measurer
  const flow = shapeTextFlowWithWrap(
    shape,
    getShapeTextFlowBoundary(shape, basis.pointPt, basis.leadingMult),
    wrap
  )

  // A frame too short for one row band lays out NOTHING — it does not lay out everything. Returning
  // `null` here made those two answers indistinguishable to `fitParagraphsToShape`, whose own `null`
  // means "it all fits", which `distributeStory` reads as "every frame after this one is drained"
  // (`chain-story.ts`). So linking an overflowing frame this small handed its downstream frame an
  // empty slice while the source kept text it cannot paint — WMPCM-9822. `consumedLength: 0` splits
  // at offset 0 instead: the frame keeps nothing and the whole story moves on, which is the only
  // answer that lets the text stay visible somewhere.
  //
  // The common way to land here is a box dragged near the minimum size; the renderer survives it via
  // `flow.fallbackRow`, but that is deliberately placeholder-only (`shape-text.ts`) so this function
  // has no equivalent and must answer in offsets.
  if (flow.rows.length === 0) return { consumedLength: 0, textLength: text.length }

  const alignments = paragraphs.map((paragraph) => paragraph.alignment ?? 0)
  const charRunMap = buildCharRunMap(paragraphs)
  // The settled pipeline, identical to the renderer's — including the reclaim pass and drop cap.
  // Measuring without the reclaim pass would report the probe grid's capacity while the canvas
  // paints the settled one, and a chain would hand off at an offset the destination never draws to;
  // measuring without drop cap would ignore the row narrowing an active cap reserves.
  const { layout } = layoutShapeTextWithPerLineLeading(
    shape,
    flow,
    text,
    measurer.measureTextPt,
    charRunMap,
    alignments,
    { breakTrailingWordAtCapacity: fillToCharacter },
    (f) => shapeTextFlowWithWrap(shape, f, wrap),
    (candidate) => {
      const dropCapResult = layoutShapeTextWithDropCap(
        text,
        candidate,
        measurer.measureTextPt,
        paragraphs,
        measurer.charRunMap,
        getSharedMeasureContext(),
        fonts,
        alignments,
        { breakTrailingWordAtCapacity: fillToCharacter }
      )

      return applyPerLineLeadingToFlow(
        dropCapResult.flow,
        dropCapResult.layout,
        charRunMap,
        measurer.rowMetricPt
      )
    }
  )

  return { consumedLength: layout.consumedLength, textLength: text.length }
}

/** Hard cap on the shrink loop below — the cap exists so a pathological flow cannot spin, not
 *  because a third pass would be wrong. */
const MAX_VERIFY_PASSES = 2

/**
 * Shrinks `offset` until the kept half lays itself out whole — see
 * `ParagraphBoxFitOptions.verifyKeptFits`.
 *
 * `paragraphsToPlainText(kept)` is always a PREFIX of `paragraphsToPlainText(paragraphs)` (the split
 * reproduces the same characters and the same paragraph-joining `\n`s), so an offset measured in the
 * kept half's own coordinates is directly usable as an offset into the full text — no translation.
 *
 * Monotone by construction: only ever takes `Math.min`. Shrinking `kept` can drop the frame's
 * largest run, which lowers `pointPt`, shortens every row and therefore *raises* capacity — so a
 * later pass can legitimately report a larger offset, and accepting it would reinstate the very
 * character this exists to remove.
 *
 * Never shrinks to nothing: an offset of 0 would empty the frame and push its whole content
 * downstream, which is a worse failure than one undrawn glyph and can oscillate against the
 * backward pull. The original offset stands in that case.
 */
function convergeKeptOffset(
  paragraphs: Paragraph[],
  shape: ShapeData,
  fonts: ParagraphBlockFontLookup | undefined,
  wrap: ShapeTextWrapContext | undefined,
  fillToCharacter: boolean | undefined,
  initialOffset: number
): number {
  let offset = initialOffset

  for (let pass = 0; pass < MAX_VERIFY_PASSES; pass++) {
    const { kept } = splitShapeTextParagraphsAtOffset(paragraphs, offset)
    const probe = measureShapeTextConsumption(kept, shape, fonts, wrap, fillToCharacter)

    // `null` means the kept half holds no text — nothing this loop can act on. Otherwise it has
    // converged once the kept half draws every character it holds.
    //
    // The zero-row shape reaches here as `consumedLength: 0` rather than `null` (WMPCM-9822), which
    // the `next <= 0` guard below already handles. It only ever arrives with `initialOffset` 0, and
    // then `kept` is empty and this probe is `null` anyway: `kept` is a PREFIX, so its largest run —
    // and therefore `pointPt`, `rowHeight` and the row count — can never be worse than the full
    // candidate's. A shape that lays out a row for the whole list still lays one out for a prefix.
    if (!probe || probe.consumedLength >= probe.textLength) break

    const next = Math.min(offset, probe.consumedLength)

    if (next <= 0 || next >= offset) break
    offset = next
  }

  return offset
}

/** Probe budget for the grow pass's fallback search, after its estimate has already bracketed the
 *  answer. Each probe is a full re-flow of the frame and this runs per keystroke through the live
 *  chain projection, so the bound is a cost ceiling; under-shooting it only leaves the split point
 *  where it already was. */
const MAX_GROW_PASSES = 5

/**
 * Pushes `offset` back out until the frame is drawing everything it can — the mirror image of
 * `convergeKeptOffset`, and the other half of "measure with the engine that draws".
 *
 * **Why the split point comes back too small in the first place.** `measureShapeTextConsumption`
 * builds its measurer from the paragraph list it is handed, and `distributeStory` hands every frame
 * the WHOLE remaining story — this frame's text plus everything destined for every later frame in
 * the chain. The row pitch is a max over all of it (`rowMetricRun`), so a run enlarged three frames
 * downstream slices THIS frame's outline into taller, fewer rows than the renderer will use, and the
 * frame reports less capacity than it has. `convergeKeptOffset` cannot undo that: it is deliberately
 * monotone-shrinking, so once text has been pushed across the seam nothing pulls it back. That is
 * QA's "some text moves to Text Box 2 even though there was enough space left in the current box"
 * (WMPCM-9070 round 5, Bug 4), and — on a shortening edit, where the reclaim has to come back
 * through the same fitter — the shape of "text stops reflowing backward" (Bug 1).
 *
 * **Why growing is sound here when accepting a larger offset inside `convergeKeptOffset` is not.**
 * That loop's hazard is accepting a number produced by probing a DIFFERENT prefix: a shorter `kept`
 * can drop the frame's largest run, which raises capacity, so its probe legitimately reports an
 * offset the current candidate would not survive. This pass never carries a number across — it
 * proposes an offset, splits at exactly that offset, and accepts it only when the resulting `kept`
 * lays itself out whole. Every accepted value has been verified as the thing being accepted, so the
 * result is a genuine fixed point of the renderer's own inputs rather than a different pass's answer.
 *
 * Monotone upward and bounded by `initialOffset`/`textLength`, so it composes with the shrink pass
 * without either being able to undo the other: `convergeKeptOffset` removes characters the renderer
 * cannot draw, and this adds only characters it verifiably can.
 */
function growKeptOffset(
  paragraphs: Paragraph[],
  shape: ShapeData,
  fonts: ParagraphBlockFontLookup | undefined,
  wrap: ShapeTextWrapContext | undefined,
  fillToCharacter: boolean | undefined,
  initialOffset: number,
  textLength: number
): number {
  // The gate, and the reason this costs nothing on an ordinary frame. The whole defect is the grid
  // being coarser for the full candidate than for the half this frame will actually draw, so when
  // the two pitches agree there is nothing to reclaim and no probe is worth paying for. A uniformly
  // styled chain — the overwhelmingly common case — always takes this branch.
  const { kept: keptAtInitial } = splitShapeTextParagraphsAtOffset(paragraphs, initialOffset)

  if (gridRowPitchPt(keptAtInitial, fonts) >= gridRowPitchPt(paragraphs, fonts) - 0.01) {
    return initialOffset
  }

  // One estimate, to bracket the search rather than binary-searching the whole tail. Flowing the FULL
  // text through the grid the frame's OWN content implies answers "how much would fit if this frame
  // were sliced the way it is actually drawn?" directly. It is only an estimate — the offset it names
  // brings new runs into the frame, which can raise the pitch again — so it is a search bound, never
  // an answer. Without it the search would span every remaining character of the story and pay a full
  // re-flow per bisection.
  const estimate = measureShapeTextConsumption(
    paragraphs,
    shape,
    fonts,
    wrap,
    fillToCharacter,
    keptAtInitial
  )

  if (!estimate || estimate.consumedLength <= initialOffset) return initialOffset

  const verifiedAt = (offset: number): boolean => {
    const { kept } = splitShapeTextParagraphsAtOffset(paragraphs, offset)
    const probe = measureShapeTextConsumption(kept, shape, fonts, wrap, fillToCharacter)

    // `null` means the kept half holds no text, which cannot happen for an offset above one that
    // already held some — treat it as unverified rather than accepting it.
    return probe != null && probe.consumedLength >= probe.textLength
  }

  // The common case: the estimate holds, and the whole pass cost two re-flows.
  if (verifiedAt(Math.min(estimate.consumedLength, textLength))) {
    return Math.min(estimate.consumedLength, textLength)
  }

  // Otherwise bisect between the offset already known good and the estimate known bad. "Fits" is
  // monotone over this range — `kept` is a PREFIX, so a longer one can only add runs, and adding runs
  // can only raise the frame's pitch (never lower it) while demanding more rows — so bisection is
  // sound here even though the pitch is content-dependent.
  let accepted = initialOffset
  let low = initialOffset
  let high = Math.min(estimate.consumedLength, textLength) - 1

  for (let pass = 0; pass < MAX_GROW_PASSES && low < high; pass++) {
    const candidate = Math.ceil((low + high + 1) / 2)

    if (candidate <= accepted) break

    if (verifiedAt(candidate)) {
      accepted = candidate
      low = candidate
    } else {
      high = candidate - 1
    }
  }

  return accepted
}

/**
 * Shape-geometry counterpart to `fitParagraphsToBox`: determines how much of `paragraphs` fits
 * inside `shape`, honouring the shape's real outline rather than its bounding box. Returns `null`
 * when everything fits — same contract as the rectangle fitter, so the two are interchangeable as
 * the per-frame fitter `chainFrameFit` installs on every `StoryFrame` (WMPCM-7988).
 *
 * Capacity is measured with the shape's own scanline engine (`getShapeTextFlow` slices the
 * flattened path into per-row horizontal segments, concavity-correct for stars and hearts;
 * `layoutShapeTextContent` flows text through those slots and reports `consumedLength`). Measuring
 * with the engine that also *renders* the text is the whole point: any other capacity model — for
 * instance inverting the contour into CSS float obstacles and reusing `fitParagraphsToBox` — would
 * hand off overflow at one offset while drawing the text to another.
 *
 * Note that shape text has no overflow concept of its own: anything past the last fitting row is
 * simply not laid out (overflow clamping was deliberately removed earlier). This function is what
 * turns that silent truncation into a chain hand-off.
 *
 * A shape too short to hold **any** row is that same truncation taken to its limit, and it returns a
 * split at offset 0 — `kept` empty, the whole story handed on — not `null` (WMPCM-9822). `null` is
 * reserved for "there is nothing to lay out" and "it all fits", the two cases where the frame keeps
 * what it was given; a frame that can paint nothing keeps nothing.
 *
 * `options.fillToCharacter` splits inside the trailing word rather than handing it across whole —
 * only legal for a frame with a next frame in its chain, see `ParagraphBoxFitOptions`.
 *
 * **`options.verifyKeptFits` closes the row-basis half of "measure with the engine that draws".**
 * This header used to argue that no verification was needed: the kept text's trailing token is by
 * construction a character prefix that fit the row it was placed on, so re-laying the kept text out
 * under the default word policy takes `layoutShapeTextContent`'s "word fits here" branch and
 * produces an identical layout — from which it concluded that "the renderer, `bakeShapeTextWireLines`
 * and every non-chain consumer need no flag of their own". The argument about the BRANCH logic is
 * sound (the whole-word test is literally the same sum of per-character advances the per-character
 * loop applies one glyph at a time), but the conclusion does not follow, because the two passes are
 * not handed the same INPUTS. The renderer measures the kept half alone, and `buildShapeTextMeasurer`
 * derives the frame's row pitch from the largest run in the list it is given — a list that here
 * still contains the runs about to flow away. When that moves the flow at all, this fitter keeps a
 * character the renderer never places, and on a chained frame nothing says so (the overflow badge is
 * ANDed with `!hasNextInChain` in `shape-text-editor/shape-text-editor.tsx`). That is QA's "the last character in
 * frame 1 is hidden at the frame boundary" (WMPCM-9070, 2026-08-24 Bug 1). Setting the flag
 * re-measures this function's own result under exactly the renderer's inputs and shrinks the split
 * until it holds.
 *
 * **What it does NOT close, and what that means for QA's report.** Both passes are handed the same
 * `fonts`, so it converges on a fixed point of THIS function's own font metrics — if the fitter ever
 * measures with a null lookup (`fontLookupRef` not yet synced) while the renderer has real ones, the
 * guard confirms the wrong offset twice. It is also, provably, a no-op for uniformly styled text:
 * with one point size throughout, `pointPt`/`leadingMult` are identical for the full candidate and
 * the kept half, the flow is therefore identical, and the kept prefix lays out whole on the first
 * pass. So this hardens the divergence CLASS — it is not a demonstrated fix for the 2026-08-24
 * report, whose repro is a uniform paste. If that symptom persists, the remaining suspects are the
 * font lookup above and the paint path (`shape-text-editor/components/shape-text-chunk.tsx`'s per-glyph spans and its
 * `clipPath`/`overflow-hidden` container), not this function; log
 * `{ consumedLength, textLength, flow.rows.length, flow.rowHeight, pointPt, leadingMult }` here and
 * in `use-shape-text-layout.ts` for the same frame and diff them before changing anything else.
 *
 * Do not turn it on for every caller. A fit used as a HISTORY PROBE — `seamWasCharacterSplit`,
 * re-deriving whether an existing seam was a character split — has to reproduce the split that
 * actually produced that seam, which for every chain already sitting in a spread is the un-guarded
 * one.
 */
export function fitParagraphsToShape(
  paragraphs: Paragraph[],
  shape: ShapeData,
  fonts?: ParagraphBlockFontLookup,
  options?: ParagraphBoxFitOptions,
  /** Text-wrap context. Omit and capacity is measured with no exclusion zones — see
   *  `shapeTextFlowWithWrap`. Must match what the renderer is given, or a chain seam drifts. */
  wrap?: ShapeTextWrapContext
): ParagraphBoxFit | null {
  if (paragraphs.length === 0) return null

  const measured = measureShapeTextConsumption(
    paragraphs,
    shape,
    fonts,
    wrap,
    options?.fillToCharacter
  )

  if (!measured) return null
  if (measured.consumedLength >= measured.textLength) return null

  if (!options?.verifyKeptFits) {
    return splitShapeTextParagraphsAtOffset(paragraphs, measured.consumedLength)
  }

  const shrunk = convergeKeptOffset(
    paragraphs,
    shape,
    fonts,
    wrap,
    options.fillToCharacter,
    measured.consumedLength
  )
  // Grown only under the same flag that verifies. Both directions are re-measurements against the
  // renderer's inputs, and a HISTORY PROBE (`seamWasCharacterSplit`) must reproduce the un-verified
  // fitter that produced the seam it is re-deriving — so neither may run there.
  const offset = growKeptOffset(
    paragraphs,
    shape,
    fonts,
    wrap,
    options.fillToCharacter,
    shrunk,
    measured.textLength
  )

  if (offset >= measured.textLength) return null

  return splitShapeTextParagraphsAtOffset(paragraphs, offset)
}
